import { useTranslations } from 'next-intl';
import { type Control, type UseFormRegister, useFormState } from 'react-hook-form';
import type { EvCalculationResponse } from '@//app/api/ev-calculation/[id]';
import { DataList, DataListItem } from '@//components/DataList';
import { Disclaimer } from '@//components/Disclaimer';
import type { SelectOption } from '@//components/Select';
import { SummaryBox } from '@//components/SummaryBox';
import { VStack } from '@//components/VStack';
import { RangeSliderSelectfield } from '@//containers/RangeSliderSelectfield';
import { Textfield } from '@//containers/Textfield';
import { WidgetDetails } from '../../WidgetDetails';
import type { EvCalculationForm } from '../utils';

interface OwnPriceProps {
  control: Control<EvCalculationForm>;
  minDistance: number;
  maxDistance: number;
  distance: number;
  distanceOptions: SelectOption[];
  defaultDistanceValue?: number;
  data?: EvCalculationResponse;
  costPerMonth?: number;
  costPerMonthLabel: string;
  selectedDistance?: string;
  loading?: boolean;
  onChangeDistanceSlider: (value: [number]) => void;
  onChangeDistanceSelect: (value: [number]) => void;
  onChangeCostInput: (value: number) => void;
  onBlurDistance: () => void;
  register: UseFormRegister<EvCalculationForm>;
}

export const OwnPrice: React.FC<OwnPriceProps> = ({
  control,
  minDistance,
  maxDistance,
  distance,
  distanceOptions,
  defaultDistanceValue,
  data,
  costPerMonth,
  costPerMonthLabel,
  selectedDistance,
  loading,
  onChangeDistanceSlider,
  onChangeDistanceSelect,
  onChangeCostInput,
  onBlurDistance,
  register,
}) => {
  const { errors } = useFormState({ control });
  const t = useTranslations('pages.item.chargingCostsSection');
  return (
    <VStack gap={24}>
      <RangeSliderSelectfield
        variant="compact"
        id="ev-charging-own-price-yearly-mileage"
        label={t('yearlyMileage')}
        rangeSlider={{
          id: 'ev-charging-own-price-yearly-mileage-range-slider',
          label: t('yearlyMileage'),
          min: minDistance,
          max: maxDistance,
          step: 500,
          defaultValue: defaultDistanceValue ? [defaultDistanceValue] : undefined,
          value: [Number.isNaN(distance) ? minDistance : distance],
          onChange: onChangeDistanceSlider,
        }}
        selectfield={{
          id: 'ev-charging-own-price-yearly-mileage-select',
          label: t('yearlyMileage'),
          defaultValue: defaultDistanceValue,
          options: distanceOptions,
          ...register('distance', {
            valueAsNumber: true,
            onBlur: onBlurDistance,
            onChange: (e) => {
              const value = Number.parseInt(e.target.value, 10);
              if (!Number.isNaN(value)) {
                onChangeDistanceSelect([value]);
              }
            },
          }),
        }}
      />
      <Textfield
        label={t('ownElectricityPrice.label')}
        id="ev-charging-own-price-electricity-price"
        description={t('ownElectricityPrice.description')}
        unit={t('ownElectricityPrice.unit')}
        min={1}
        step={1}
        type="number"
        state={errors.kwhCost ? 'error' : undefined}
        stateMessage={errors.kwhCost?.message}
        {...register('kwhCost', {
          valueAsNumber: true,
          onChange: (e) => {
            const value = Number.parseFloat(e.target.value);
            if (!Number.isNaN(value)) {
              onChangeCostInput(value);
            }
          },
        })}
      />
      <SummaryBox
        label={t('chargingCost')}
        value={loading ? t('loading') : costPerMonthLabel}
        ariaBusy={loading}
      />
      <WidgetDetails
        id="ev-charging-own-price-details"
        distance={selectedDistance}
        chargingCost={costPerMonth}
      >
        <Disclaimer>{t('disclaimers.ownPrice')}</Disclaimer>
        <DataList>
          <DataListItem
            label={t('details.vehicleBatteryCapacity')}
            value={
              data
                ? `${data.vehicle.batteryCapacity.value} ${data.vehicle.batteryCapacity.unit}`
                : t('details.noValue')
            }
          />
          <DataListItem
            label={t('details.vehicleRange')}
            value={
              data
                ? `${data.vehicle.electricalRangeWLTP.value} ${data.vehicle.electricalRangeWLTP.unit}`
                : t('details.noValue')
            }
          />
          <DataListItem
            label={t('details.specifiedYearlyMileage')}
            value={selectedDistance ? selectedDistance : t('details.noValue')}
          />
        </DataList>
      </WidgetDetails>
    </VStack>
  );
};
